<script setup>
import { computed, reactive, ref, shallowRef } from 'vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import SettingsSection from './SettingsSection.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { matches, required, strongPassword } from '@/utils/validation';

// Settings → Password: current password, then the new one twice.
const toast = useToast();
const formRef = ref(null);
const form = reactive({ current: '', password: '', confirm: '' });
const reveal = shallowRef(false);
const saving = shallowRef(false);
const savedAt = shallowRef(0);
const currentError = shallowRef('');

const dirty = computed(() => Boolean(form.current || form.password || form.confirm));

function reset() {
  Object.assign(form, { current: '', password: '', confirm: '' });
  currentError.value = '';
  formRef.value?.resetValidation();
}

async function save() {
  currentError.value = '';
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  saving.value = true;
  try {
    const body = await apiRequest('post', '/auth/changePass', {
      old_password: form.current,
      new_password: form.password,
    });
    if (Number(body?.status) === 400 && /invalid password/i.test(body.msg || '')) {
      currentError.value = 'This is not your current password.';
      return;
    }
    assertOk(body);
    reset();
    savedAt.value = Date.now();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not update your password.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <SettingsSection
    title="Update password"
    description="Use a long, unique password to keep your account secure."
    :dirty="dirty"
    :saving="saving"
    :saved-at="savedAt"
    @save="save"
    @reset="reset"
  >
    <v-form ref="formRef" class="form-stack" @submit.prevent="save">
      <UiFormField label="Current password" v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.current"
          :type="reveal ? 'text' : 'password'"
          :rules="[required]"
          :error-messages="currentError"
          autocomplete="current-password"
          hide-details="auto"
          @update:model-value="currentError = ''"
        />
      </UiFormField>
      <UiFormField
        label="New password"
        description="At least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol."
        v-slot="{ id }"
      >
        <v-text-field
          :id="id"
          v-model="form.password"
          :type="reveal ? 'text' : 'password'"
          :append-inner-icon="reveal ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :rules="[required, strongPassword]"
          autocomplete="new-password"
          hide-details="auto"
          @click:append-inner="reveal = !reveal"
        />
      </UiFormField>
      <UiFormField label="Confirm password" v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.confirm"
          :type="reveal ? 'text' : 'password'"
          :rules="[required, matches(() => form.password, 'Passwords do not match.')]"
          autocomplete="new-password"
          hide-details="auto"
        />
      </UiFormField>
    </v-form>
  </SettingsSection>
</template>
